import { useEffect, useState } from 'react';
import { ILogEntry, ILogResponse, LogLevel } from '@skystone/shared';
import { logApi } from '@/services/log-api';
import { formatDateTime } from '@/lib/date-utils';
import { getErrorMessage } from '@/lib/utils';
import { Badge, PageLoading, ErrorBanner } from '@/components/ui';
import { DataTable, ColumnDef } from '@/components/ui/DataTable';

const LOG_LIMIT = 200;

const levelVariant = (level: LogLevel): 'error' | 'warning' | 'secondary' | 'default' => {
  switch (level) {
    case 'ERROR': return 'error';
    case 'WARN': return 'warning';
    default: return 'secondary';
  }
};

const columns: ColumnDef<ILogEntry>[] = [
  {
    id: 'timestamp',
    header: 'Timestamp',
    accessor: 'timestamp',
    cell: (val) => formatDateTime(val as string),
  },
  {
    id: 'level',
    header: 'Level',
    accessor: 'level',
    cell: (val) => {
      const level = val as LogLevel;
      return <Badge variant={levelVariant(level)}>{level}</Badge>;
    },
    filter: {
      type: 'select',
      options: [
        { label: 'DEBUG', value: 'DEBUG' },
        { label: 'INFO', value: 'INFO' },
        { label: 'WARN', value: 'WARN' },
        { label: 'ERROR', value: 'ERROR' },
      ],
    },
  },
  {
    id: 'source',
    header: 'Source',
    accessor: 'source',
    cell: (val) => {
      const src = val as string | undefined;
      if (!src) return '—';
      return <Badge variant="secondary">{src}</Badge>;
    },
    filter: {
      type: 'select',
      options: [
        { label: 'Backend', value: 'backend' },
        { label: 'Worker', value: 'worker' },
      ],
    },
  },
  {
    id: 'feature',
    header: 'Feature',
    accessor: 'feature',
    filter: { type: 'text', placeholder: 'Search feature...' },
  },
  {
    id: 'message',
    header: 'Message',
    accessor: 'message',
  },
  {
    id: 'value',
    header: 'Value',
    accessor: 'value',
    cell: (val) => {
      if (val === undefined || val === null) return '—';
      const text = typeof val === 'string' ? val : JSON.stringify(val);
      return (
        <span className="max-w-xs truncate text-xs text-muted-foreground" title={text}>
          {text}
        </span>
      );
    },
  },
];

export function ApplicationLogsTab() {
  const [data, setData] = useState<ILogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    // Guard against out-of-order responses: a slow request for an earlier
    // filter state must not overwrite the results of a newer one.
    let cancelled = false;

    const fetchLogs = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const level = filters.level as LogLevel | undefined;
        const search = filters.feature || undefined;
        const source = filters.source || undefined;
        const response = await logApi.getLogs({ level, search, source, limit: LOG_LIMIT });
        if (!cancelled) setData(response);
      } catch (err: unknown) {
        if (!cancelled) setError(getErrorMessage(err, 'Failed to load logs'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchLogs();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const handleFilterChange = (newFilters: Record<string, string>): void => {
    setFilters(newFilters);
  };

  if (loading && !data) {
    return <PageLoading />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {`View structured application logs. Showing latest ${LOG_LIMIT} entries.`}
      </p>

      <ErrorBanner message={error} />

      <DataTable
        data={data?.data ?? []}
        columns={columns}
        keyExtractor={(entry) => `${entry.timestamp}-${entry.feature}-${entry.message}`}
        isLoading={loading}
        emptyMessage="No log entries found."
        enablePagination={false}
        enableFilters={true}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
}
